import React, { useState, useEffect, useRef, useContext } from "react";
import { toast } from "react-toastify";
import { UNSAFE_NavigationContext as NavigationContext } from "react-router-dom";
import BCHeader from "../../../shared/BCHeader";
import { HStack, VStack } from "../../../components/utils";
import { Stepper, Step } from "../../../components/shared/Stepper";
import { StepperNavigation } from "../../../components/shared/StepperNavigation";
import { extractVariables } from "../../../api/customEmail";
import { CampaignState, ParsedRow } from "./types";
import Step1Upload from "./Step1Upload";
import Step2Edit from "./Step2Edit";
import Step3Variables from "./Step3Variables";
import Step4Upload from "./Step4Upload";
import Step5Preview from "./Step5Preview";
import AlertDialog from "../../../components/shared/AlertDialog";

const STEPS: Step[] = [
  {
    stepNo: 1,
    title: "Upload Template",
    description: "Upload DOCX or HTML file",
  },
  {
    stepNo: 2,
    title: "Edit Template",
    description: "Edit content in rich text editor",
  },
  {
    stepNo: 3,
    title: "Review Variables",
    description: "Check system & custom variables",
  },
  {
    stepNo: 4,
    title: "Upload Data",
    description: "Download & fill employee data",
  },
  {
    stepNo: 5,
    title: "Preview & Send",
    description: "Preview and send emails",
  },
];

export default function CustomEmailCampaign() {
  const [currentStep, setCurrentStep] = useState(1);
  const [campaignState, setCampaignState] = useState<CampaignState>({
    htmlContent: "",
    subject: "",
    systemVars: [],
    customVars: [],
    suspiciousVars: [],
    dataRows: [],
  });
  const [isDirty, setIsDirty] = useState(true);
  const [showLeaveGuard, setShowLeaveGuard] = useState(false);
  const pendingNavRef = useRef<(() => void) | null>(null);
  const isLeavingRef = useRef(false);
  const { navigator } = useContext(NavigationContext);

  useEffect(() => {
    if (!isDirty) return undefined;
    const nav = navigator as any;
    const originalPush = nav.push?.bind(nav);
    const originalReplace = nav.replace?.bind(nav);

    const intercept =
      (original: (...args: any[]) => void) =>
      (...args: any[]) => {
        if (isLeavingRef.current) {
          original(...args);
        } else {
          pendingNavRef.current = () => {
            isLeavingRef.current = true;
            original(...args);
          };
          setShowLeaveGuard(true);
        }
      };

    if (originalPush) nav.push = intercept(originalPush);
    if (originalReplace) nav.replace = intercept(originalReplace);

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      if (originalPush) nav.push = originalPush;
      if (originalReplace) nav.replace = originalReplace;
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty, navigator]);

  const handleLeave = () => {
    setShowLeaveGuard(false);
    setIsDirty(false);
    pendingNavRef.current?.();
    pendingNavRef.current = null;
  };

  const handleStay = () => {
    setShowLeaveGuard(false);
    pendingNavRef.current = null;
  };

  const handleSendSuccess = () => {
    isLeavingRef.current = true;
    setIsDirty(false);
  };

  const goTo = (step: number) => setCurrentStep(step);

  const handleStep1Next = (html: string, subject?: string) => {
    setCampaignState((prev) => ({
      ...prev,
      htmlContent: html,
      ...(subject !== undefined ? { subject } : {}),
    }));
    goTo(2);
  };

  const handleStep2Next = async (html: string) => {
    setCampaignState((prev) => ({ ...prev, htmlContent: html }));
    try {
      const result = await extractVariables(html);
      setCampaignState((prev) => ({
        ...prev,
        htmlContent: html,
        systemVars: result.systemVars,
        customVars: result.customVars,
        suspiciousVars: result.suspiciousVars ?? [],
      }));
    } catch {
      toast.error("Failed to extract variables. Continuing anyway.");
    }
    goTo(3);
  };

  const handleStep4Next = (rows: ParsedRow[]) => {
    setCampaignState((prev) => ({ ...prev, dataRows: rows }));
    goTo(5);
  };

  const steps = [
    {
      stepNo: 1,
      component: <Step1Upload onNext={handleStep1Next} />,
    },
    {
      stepNo: 2,
      component: (
        <Step2Edit
          htmlContent={campaignState.htmlContent}
          subject={campaignState.subject}
          onSubjectChange={(s) =>
            setCampaignState((prev) => ({ ...prev, subject: s }))
          }
          onNext={handleStep2Next}
          onBack={() => goTo(1)}
        />
      ),
    },
    {
      stepNo: 3,
      component: (
        <Step3Variables
          htmlContent={campaignState.htmlContent}
          systemVars={campaignState.systemVars}
          customVars={campaignState.customVars}
          suspiciousVars={campaignState.suspiciousVars}
          onNext={() => goTo(4)}
          onBack={() => goTo(2)}
        />
      ),
    },
    {
      stepNo: 4,
      component: (
        <Step4Upload
          customVars={campaignState.customVars}
          initialRows={campaignState.dataRows}
          onNext={handleStep4Next}
          onBack={() => goTo(3)}
        />
      ),
    },
    {
      stepNo: 5,
      component: (
        <Step5Preview
          state={campaignState}
          onBack={() => goTo(4)}
          onSendSuccess={handleSendSuccess}
        />
      ),
    },
  ];

  return (
    <VStack className="h-full gap-4">
      <BCHeader
        className="items-baseline"
        bcTitle="Stakeholders"
        bcSubTitle="Custom Email"
      />

      <HStack className="flex h-full min-h-full gap-4">
        <VStack className="flex w-[25%] bg-white rounded-lg min-w-64 drop-shadow-md">
          <Stepper
            steps={STEPS}
            currentStep={currentStep}
            onStepClick={() => {}}
            className="h-full min-h-[400px]"
          />
        </VStack>

        <VStack
          className="flex w-[75%] bg-white rounded-lg min-w-96 drop-shadow-md"
          style={{ minHeight: "calc(100vh - 180px)" }}
        >
          <StepperNavigation
            currentStep={currentStep}
            steps={steps}
            className="p-0"
          />
        </VStack>
      </HStack>

      <AlertDialog
        open={showLeaveGuard}
        onClose={handleStay}
        message="All your data will be lost if you leave. Are you sure you want to leave?"
        primaryActionText="Leave"
        secondaryActionText="Stay"
        onPrimaryAction={handleLeave}
        onSecondaryAction={handleStay}
      />
    </VStack>
  );
}
